import React from 'react';
import { FaFacebook, FaTiktok, FaInstagram, FaLinkedin } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import useDarkMode from '../../../hooks/useDarkMode';
import Footer from '../../../layout/Footer/Footer';
import { SOCIAL_LINKS } from '../../../components/seo/siteConfig';
import { LANDING_PAGES } from '../../../content/landing';

/** Icon per social network. Keys match `name` in SOCIAL_LINKS. */
const SOCIAL_ICONS: Record<string, { Icon: React.ComponentType<{ size?: number; color?: string }>; color: string }> = {
  Facebook: { Icon: FaFacebook, color: '#3b5998' },
  Instagram: { Icon: FaInstagram, color: '#E1306C' },
  TikTok: { Icon: FaTiktok, color: '#000000' },
  LinkedIn: { Icon: FaLinkedin, color: '#0077B5' },
};

const DefaultFooter = () => {
  const { darkModeStatus } = useDarkMode();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  return (
    <Footer>
      <div className='container'>
        <hr style={{ opacity: 0.25 }} />
        <div className='row'>
          {/* Social Media Links */}
          <div className='col-md-4 d-flex flex-column align-items-md-start'>
            <h5>{t('Follow Us')}</h5>
            <ul className='list-unstyled d-flex' style={{ paddingLeft: 0, paddingRight: 0 }}>
              {SOCIAL_LINKS.map(({ name, url }) => {
                const entry = SOCIAL_ICONS[name];
                if (!entry) return null;
                const { Icon, color } = entry;
                return (
                  <li className='me-3' key={name}>
                    <a href={url} target='_blank' rel='noopener noreferrer' aria-label={name}>
                      <Icon size={24} color={color} />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Quick Links */}
          <div className='col-md-4 d-flex flex-column align-items-md-center mt-3'>
          <h5>{t('Quick Links')}</h5>
            <ul className='list-unstyled d-flex flex-column align-items-md-start' style={{ paddingLeft: 0, paddingRight: 0 }}>
              <li style={{ marginTop: 2 }}><a href='/'>{t('Home')}</a></li>
              <li style={{ marginTop: 2 }}><a href='/auth'>{t('Account')}</a></li>
              <li style={{ marginTop: 2 }}><a href='/products'>{t('Products')}</a></li>
              <li style={{ marginTop: 2 }}><a href='/blog'>{t('Blog')}</a></li>
              <li style={{ marginTop: 2 }}><a href='mailto:info@storiesvillage.com'>{t('Contact')}</a></li>
              <li style={{ marginTop: 2 }}><a href='/privacy'>{t('Privacy Policy')}</a></li>
              <li style={{ marginTop: 2 }}><a href='/terms'>{t('Terms of Service')}</a></li>
              <li style={{ marginTop: 2 }}><a href='/terms-of-sale'>{t('Terms of Sale')}</a></li>
              <li style={{ marginTop: 2 }}><a href='/refund-policy'>{t('Refund Policy')}</a></li>
            </ul>
          </div>

          {/* Contact Us Form */}
          <div className={`col-md-4 d-flex flex-column align-items-md-end mt-3 ${isRtl ? 'rtl-fix' : ''}`}>
            <h5>{t('Contact Us')}</h5>
            <h6><a href='mailto:info@storiesvillage.com'>info@storiesVillage.com</a></h6>
          </div>
        </div>

        <div className='row mt-4'>
          <div className='d-flex align-items-center justify-content-center' style={{ marginBottom: 10 }}>
            <span className='fw-light'>{t('StoriesVillage© 2025 - all rights reserved')}</span>
          </div>
        </div>

        {/*
         * Gift-guide links: crawl path into the editorial landing pages, kept
         * out of sight (same visually-hidden approach as ComingSoonPage's
         * footer) rather than shown as a visible column — they're already
         * listed in sitemap.xml regardless, this is purely for crawlers that
         * follow links instead of/alongside reading the sitemap.
         */}
        <nav className='visually-hidden' aria-label={t('Gift guides')}>
          {LANDING_PAGES.map((p) => (
            <a key={p.path} href={p.path}>{p.heading}</a>
          ))}
        </nav>
      </div>
    </Footer>
  );
};

export default DefaultFooter;